import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { create } from "zustand";
import { useEffect } from "react";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

interface Project {
  id: string;
  [key: string]: any;
}

interface ProjectSystemPrompt {
  project_id: string;
  prompt: string;
}

interface CustomKnowledgeStore {
  projectSystemPrompts: Record<string, ProjectSystemPrompt>;
  currentPrompt: string;
  hasUnsavedChanges: boolean;
  setProjectSystemPrompt: (projectId: string, prompt: string) => void;
  deleteProjectSystemPrompt: (projectId: string) => void;
  setCurrentPrompt: (prompt: string) => void;
  resetUnsavedChanges: () => void;
}

// Create a Zustand store for project system prompts
const useCustomKnowledgeStore = create<CustomKnowledgeStore>((set, get) => ({
  projectSystemPrompts: {},
  currentPrompt: "",
  hasUnsavedChanges: false,
  setProjectSystemPrompt: (projectId, prompt) =>
    set((state) => ({
      projectSystemPrompts: {
        ...state.projectSystemPrompts,
        [projectId]: { project_id: projectId, prompt },
      },
      hasUnsavedChanges: false,
    })),
  deleteProjectSystemPrompt: (projectId) =>
    set((state) => {
      const newPrompts = { ...state.projectSystemPrompts };
      delete newPrompts[projectId];
      return {
        projectSystemPrompts: newPrompts,
        currentPrompt: "",
        hasUnsavedChanges: false,
      };
    }),
  setCurrentPrompt: (prompt) =>
    set((state) => {
      // Check if the current project has a stored prompt to compare with
      const currentProjectId =
        get().projectSystemPrompts &&
        Object.keys(get().projectSystemPrompts).length > 0
          ? Object.keys(get().projectSystemPrompts)[0]
          : null;

      const storedPrompt = currentProjectId
        ? get().projectSystemPrompts[currentProjectId]?.prompt
        : "";

      return {
        currentPrompt: prompt,
        hasUnsavedChanges: prompt !== storedPrompt,
      };
    }),
  resetUnsavedChanges: () =>
    set((state) => {
      // Reset to the stored prompt for the current project
      const currentProjectId = Object.keys(state.projectSystemPrompts)[0];
      const storedPrompt = currentProjectId
        ? state.projectSystemPrompts[currentProjectId]?.prompt
        : "";

      return {
        currentPrompt: storedPrompt,
        hasUnsavedChanges: false,
      };
    }),
}));

export const useCustomKnowledge = (projectId: string | null) => {
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const {
    projectSystemPrompts,
    currentPrompt,
    hasUnsavedChanges,
    setProjectSystemPrompt,
    deleteProjectSystemPrompt,
    setCurrentPrompt,
    resetUnsavedChanges,
  } = useCustomKnowledgeStore();

  // Initialize from store or fetch from Supabase
  useEffect(() => {
    if (!supabaseClient || !projectId) return;

    // Check if we already have the prompt in the store
    const storedPrompt = projectSystemPrompts[projectId];
    if (storedPrompt) {
      // Only update if different
      if (currentPrompt !== storedPrompt.prompt) {
        setCurrentPrompt(storedPrompt.prompt);
      }
      return;
    }

    // Otherwise fetch from Supabase
    const fetchCustomKnowledge = async () => {
      try {
        const { data, error } = await supabaseClient
          .from("project_system_prompt")
          .select("prompt")
          .eq("project_id", projectId);

        if (error) throw error;

        const prompt = data[0]?.prompt || "";

        // Update the store and set current prompt
        if (prompt) {
          setProjectSystemPrompt(projectId, prompt);
          // Also directly set the current prompt to ensure it's displayed immediately
          setCurrentPrompt(prompt);
        } else {
          setCurrentPrompt("");
        }
      } catch (error) {
        console.error("Error fetching custom knowledge:", error);
      }
    };

    fetchCustomKnowledge();
  }, [projectId, supabaseClient, setProjectSystemPrompt]);

  // Save to Supabase and update store
  const savePrompt = async (promptToSave: string) => {
    if (!supabaseClient || !projectId) return false;

    try {
      const { error } = await supabaseClient
        .from("project_system_prompt")
        .upsert(
          {
            project_id: projectId,
            prompt: promptToSave,
          },
          { onConflict: "project_id" }
        );

      if (error) throw error;

      // Update the store after successful API call
      setProjectSystemPrompt(projectId, promptToSave);
      return true;
    } catch (error) {
      console.error("Error saving custom knowledge:", error);
      return false;
    }
  };

  // Reset prompt (delete from Supabase and update store)
  const resetPrompt = async () => {
    if (!supabaseClient || !projectId) return false;

    try {
      const { error } = await supabaseClient
        .from("project_system_prompt")
        .delete()
        .eq("project_id", projectId);

      if (error) throw error;

      // Update the store
      deleteProjectSystemPrompt(projectId);
      return true;
    } catch (error) {
      console.error("Error resetting custom knowledge:", error);
      return false;
    }
  };

  // Cancel changes
  const cancelChanges = () => {
    resetUnsavedChanges();
  };

  return {
    currentPrompt,
    setCurrentPrompt,
    hasUnsavedChanges,
    savePrompt,
    resetPrompt,
    cancelChanges,
  };
};
